import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import Animated, { Extrapolate, interpolate, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { AppView } from 'design-system/AppView';
import ScrollView from 'design-system/ScrollView';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import { deleteGroupTransaction, getGroupById, getGroupTransactionReceipts, patchGroupTransaction, setSpendingGroupInfo, uploadGroupTransactionReceipts, } from 'actions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectGroupAnalytics, selectGroupTransactionById, } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import TransactionHeader, { useTransactionHeaderHeight, } from 'features/transactions/components/detail/TransactionHeader';
import TransactionDateButton from 'features/transactions/components/detail/TransactionDateButton';
import { CardView, SettingsItem, SettingsItemNavigate } from 'design-system';
import { IconTrash, IconUser } from 'design-system/icons';
import { noop } from 'lodash';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Modal from 'utils/packages/Modal';
import TransactionReceiptItem from 'features/transactions/components/detail/TransactionReceiptItem';
import { CustomSplitType } from 'utils/types/navigation';
import { getUserIds } from './utils/getUserIds';
import GroupTransactionSplit from './components/GroupTransactionSplit';
import { AmountSplit } from './utils/amountSplit';
import { GroupTransactionLogo } from './components/GroupTransactionLogo';
const shadowOpacityOutputRange = [0, 0, 1];
const GroupTransactionScreen = ({ route, navigation, }) => {
    const { groupId, transactionId } = route.params;
    const groupAnalytics = useAppSelector((store) => selectGroupAnalytics(store, groupId));
    const transaction = useAppSelector((store) => selectGroupTransactionById(store, groupId, transactionId));
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        if (!transaction || !groupAnalytics) {
            navigation.pop();
            dispatch(getGroupById(groupId));
        }
    }, [groupAnalytics, groupId, transaction]);
    if (!transaction || !groupAnalytics)
        return null;
    return (<GroupTransactionScreenContent route={route} navigation={navigation} transaction={transaction} analytics={groupAnalytics}/>);
};
const GroupTransactionScreenContent = ({ navigation, route, transaction, analytics, }) => {
    const { groupId } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const isEditable = !transaction.paymentId;
    const didHandleStale = useRef(false);
    const handleStaleTransaction = useCallback(async (result) => {
        const isStale404 = result?.errorType === 'ApiError' && result?.payload?.status === 404;
        if (!isStale404)
            return false;
        if (!didHandleStale.current) {
            didHandleStale.current = true;
            await dispatch(getGroupById(groupId));
            navigation.pop();
        }
        return true;
    }, [groupId]);
    const conversionRate = useMemo(() => transaction.convertedAmount
        ? transaction.convertedAmount / transaction.amount
        : undefined, [transaction.amount, transaction.convertedAmount]);
    const transactionForHeader = useMemo(() => ({
        ...transaction,
        amount: transaction.isSettlement
            ? Math.abs(transaction.amount)
            : transaction.amount,
        convertedAmount: transaction.isSettlement && transaction.convertedAmount
            ? Math.abs(transaction.convertedAmount)
            : transaction.convertedAmount,
    }), [transaction]);
    const usersByIds = useMemo(() => getUserIds(analytics?.users || []), [analytics]);
    const paidByUser = useMemo(() => analytics?.users.find((u) => u.id === transaction.paidBy), [analytics?.users, transaction.paidBy]);
    const payer = useMemo(() => usersByIds[transaction.paidBy], [transaction.paidBy, usersByIds]);
    const payee = useMemo(() => transaction.isSettlement
        ? usersByIds[Number(Object.keys(transaction.split)[0])]
        : undefined, [transaction.isSettlement, transaction.split, usersByIds]);
    const onDelete = useCallback(() => {
        if (!isEditable)
            return;
        Modal.showConfirmation({
            title: 'Delete transaction',
            subtitle: 'Are you sure you want to delete this transaction?',
            confirmButtonTitle: 'Cancel',
            cancelButtonTitle: 'Yes, let me delete',
            onPressCancel: async () => {
                const result = await dispatch(deleteGroupTransaction(transaction.groupId, transaction.id));
                if (await handleStaleTransaction(result))
                    return;
                const txId = transaction.originalTransactionId;
                const cached = txId
                    ? store.getState().transactions.transactionCache?.[txId]
                    : undefined;
                const existing = (cached && 'spendingGroupInfo' in cached
                    ? cached.spendingGroupInfo?.groups
                    : undefined) ?? [];
                const remaining = existing.filter((g) => g.transaction.id !== transaction.id);
                dispatch(setSpendingGroupInfo(transaction.originalTransactionId, remaining.length ? { groups: remaining } : undefined));
                await dispatch(getGroupById(groupId));
            },
        });
    }, [
        isEditable,
        groupId,
        transaction.groupId,
        transaction.id,
        transaction.originalTransactionId,
        handleStaleTransaction,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const onChangeDate = useCallback(() => {
        if (!isEditable)
            return;
        navigation.navigate('EditDate', {
            groupTxn: transaction,
            onSave: async (date) => {
                const result = await dispatch(patchGroupTransaction(groupId, transaction.id, { date }));
                if (await handleStaleTransaction(result))
                    return;
                navigation.pop();
            },
        });
    }, [isEditable, groupId, transaction, handleStaleTransaction]);
    const onRenameTransaction = useCallback(() => {
        if (!transaction || !isEditable)
            return;
        navigation.navigate('RenameTransaction', {
            groupTxn: transaction,
            onSave: async (counterpartName) => {
                const result = await dispatch(patchGroupTransaction(groupId, transaction.id, { counterpartName }));
                if (await handleStaleTransaction(result))
                    return;
                navigation.pop();
            },
        });
    }, [isEditable, groupId, transaction, handleStaleTransaction]);
    const scrollY = useSharedValue(0);
    const [transactionHeaderHeightExpanded, transactionHeaderHeightFinal, scrollOffsetHeight, headerScrollOffset,] = useTransactionHeaderHeight(transaction, false, false);
    const animationInputRange = useMemo(() => [0, scrollOffsetHeight, scrollOffsetHeight + 1], [scrollOffsetHeight]);
    const bgColorOutputRange = useMemo(() => [
        colors.background.dark,
        colors.background.dark,
        colors.cards.onDark,
    ], [colors.background.dark, colors.cards.onDark]);
    const translateStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(scrollY.value, [0, headerScrollOffset, headerScrollOffset + 1], [0, -headerScrollOffset, -headerScrollOffset], Extrapolate.CLAMP),
            },
        ],
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerScrollOffset]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: () => (<Animated.View style={translateStyles}>
          <TransactionDateButton transaction={transaction} onPressDate={onChangeDate} disabled={!isEditable}/>
        </Animated.View>),
            headerRight: () => transaction && isEditable ? (<TouchableOpacity hoverOnDark onCard onPress={onRenameTransaction}>
            <Text Text-14>Rename</Text>
          </TouchableOpacity>) : null,
            title: transaction.counterpartName,
            headerTransparent: true,
            headerTintColor: colors.text.primary,
        });
    }, [
        isEditable,
        colors.text.primary,
        onChangeDate,
        onRenameTransaction,
        transaction,
        translateStyles,
    ]);
    const onSaveCustomSplit = useCallback(async (split) => {
        const newSplit = AmountSplit.toNumber(split);
        const result = await dispatch(patchGroupTransaction(groupId, transaction.id, { split: newSplit }));
        await handleStaleTransaction(result);
    }, [groupId, transaction.id, handleStaleTransaction]);
    const sortedBySplitUsers = useMemo(() => {
        if (!analytics?.users)
            return [];
        const usersToSort = [...analytics.users];
        usersToSort.sort((a, b) => Math.abs(transaction.split[b.id] || 0) -
            Math.abs(transaction.split[a.id] || 0));
        return usersToSort.filter((u) => !(!transaction.split[u.id] && u.deleted));
    }, [analytics?.users, transaction.split]);
    const formattedAmountSplit = useMemo(() => {
        const allUsersSplit = analytics?.users.reduce((acc, curr) => {
            const split = transaction.split[curr.id];
            return {
                ...acc,
                [curr.id]: split ?? 0,
            };
        }, {}) || {};
        return AmountSplit.fromShareSplit(transaction.amount, AmountSplit.toString(allUsersSplit));
    }, [analytics?.users, transaction.amount, transaction.split]);
    useEffect(() => {
        let isActive = true;
        if (!transaction.isSettlement && isEditable) {
            dispatch(getGroupTransactionReceipts(groupId, transaction.id)).then(async (result) => {
                if (!isActive)
                    return;
                await handleStaleTransaction(result);
            });
        }
        return () => {
            isActive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, transaction.id]);
    const onUploadReceipt = useCallback(async (transactionId, body, files) => {
        const result = await dispatch(uploadGroupTransactionReceipts(groupId, transactionId, body, files));
        await handleStaleTransaction(result);
    }, [groupId, handleStaleTransaction]);
    if (!paidByUser || !analytics)
        return null;
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView secondary finalHeaderHeight={transactionHeaderHeightFinal} expandedHeaderHeight={transactionHeaderHeightExpanded} animationInputRange={animationInputRange} shadowOpacityOutputRange={shadowOpacityOutputRange} bgColorOutputRange={bgColorOutputRange} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyles} scrollY={scrollY}>
        <CardView noMarginTop noMarginBottom>
          <SettingsItemNavigate disabled description="Paid by" title={payer} icon={<IconUser />}/>
          {payee ? (<SettingsItemNavigate disabled description="Received by" title={payee} icon={<IconUser />}/>) : null}
          {!transaction.isSettlement && isEditable ? (<TransactionReceiptItem transaction={transaction} isGroup onUpload={onUploadReceipt}/>) : null}
          {isEditable ? (<SettingsItem title="Delete transaction" icon={<IconTrash colorKey="negative"/>} onPress={onDelete}/>) : null}
        </CardView>

        {!transaction.isSettlement && isEditable ? (<GroupTransactionSplit disabled users={sortedBySplitUsers} total={transaction.amount} currency={transaction.currency} onSave={onSaveCustomSplit} amountSplit={formattedAmountSplit} splitType={CustomSplitType.AMOUNT} paidBy={paidByUser} conversionRate={conversionRate} convertedCurrency={analytics.currency} onSelect={noop} isDefaultSplit={false}/>) : null}
      </ScrollView>
      <TransactionHeader scrollY={scrollY} headerScrollOffset={headerScrollOffset} transaction={transactionForHeader} showRenameUpgradeButton={false} showFirstUpgradeButton={false} useCustomName onPressUpgrade={noop} customLogo={<GroupTransactionLogo R70 transaction={transaction}/>}/>
    </AppView>);
};
export default GroupTransactionScreen;
